import axios, { AxiosError } from 'axios';

import { PLATFORM_API_URL } from '../constants';
import { SERVER_ERROR_MESSAGE } from '../errors';
import { getAuthToken } from '../getAuthToken';
import { del, get, getPublic, patch, post, put } from '../requestMethods';
import { RecordingChunkUploadError } from './errors';
import {
  CompositionsResponse,
  CreateRecordingRequest,
  Recording,
  RecordingCaptureEvent,
  RecordingCompositionsResponse,
  RecordingEditorDataResponse,
  RecordingLiveUploadChunkResponse,
  RecordingLiveUploadCompleteResponse,
  RecordingLiveUploadInitResponse,
  RecordingRenderRequest,
  RecordingRenderResponse,
  RecordingRetryResponse,
  RecordingSharedSchema,
  RecordingSharingResponse,
  RecordingUploadFinishResponse,
  RecordingUploadTrack,
} from './types';

const URL = `${PLATFORM_API_URL}/api/recording/v1/recordings`;

type UploadRecordingChunkParams = {
  blob: Blob;
  chunkIndex: number;
  recordingId: string;
  signal?: AbortSignal;
  track: RecordingUploadTrack;
};

const uploadRecordingChunk = async ({
  blob,
  chunkIndex,
  recordingId,
  signal,
  track,
}: UploadRecordingChunkParams): Promise<RecordingLiveUploadChunkResponse> => {
  const token = await getAuthToken();
  const response = await fetch(`${URL}/${recordingId}/uploads/${track}/chunks/${chunkIndex}`, {
    body: blob,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'video/webm',
      'X-Upload-Content-Length': String(blob.size),
    },
    method: 'POST',
    signal,
  });

  if (response.ok) return response.json() as Promise<RecordingLiveUploadChunkResponse>;

  throw new RecordingChunkUploadError(response.status, await response.text());
};

const FINISH_REQUEST_TIMEOUT_MS = 120_000;

const finishClient = axios.create({ timeout: FINISH_REQUEST_TIMEOUT_MS });

const finishRecordingUpload = ({
  events = [],
  recordingDurationS,
  recordingId,
}: {
  events?: RecordingCaptureEvent[];
  recordingDurationS: number;
  recordingId: string;
}) =>
  new Promise<RecordingUploadFinishResponse>((resolve, reject) => {
    const sendFinish = (token: string) =>
      finishClient
        .post<RecordingUploadFinishResponse>(
          `${URL}/upload/finish`,
          { events: { events, version: '1' }, recording_id: recordingId },
          {
            headers: { authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            params: { recording_duration_s: recordingDurationS },
          },
        )
        .then((response) => resolve(response.data))
        .catch((error: AxiosError) => {
          if (error.response) {
            const { data, status, statusText } = error.response;
            reject({ data, status, statusText });
            return;
          }
          if (error.request && error.code === AxiosError.ERR_NETWORK) {
            reject({ data: null, status: 504, statusText: 'Gateway Timeout' });
            return;
          }
          reject(new Error(SERVER_ERROR_MESSAGE));
        });

    void import('../getAuthToken')
      .then((authTokenModule) => authTokenModule.getAuthToken())
      .then(sendFinish, () => reject({ data: null, status: 0, statusText: 'Session invalidated' }));
  });

const recordingSpaceParams = (spaceId?: string) => (spaceId ? { space_id: spaceId } : undefined);

export const RECORDING_API = {
  abortRecordingUpload: (recordingId: string, spaceId?: string) =>
    post<RecordingUploadFinishResponse>(`${URL}/${recordingId}/uploads/abort`, {
      params: recordingSpaceParams(spaceId),
    }),
  batchDeleteRecordings: (recordingIds: string[]) =>
    post<{ deleted_count: number }>(`${URL}/batch-delete`, { data: { recording_ids: recordingIds } }),
  completeRecordingUpload: (recordingId: string, tracks: RecordingUploadTrack[], spaceId?: string) =>
    post<RecordingLiveUploadCompleteResponse>(`${URL}/${recordingId}/uploads/complete`, {
      data: { tracks },
      params: recordingSpaceParams(spaceId),
    }),
  finishRecordingUpload,
  initRecordingUpload: (recordingId: string, hasCamera: boolean, spaceId?: string) =>
    post<RecordingLiveUploadInitResponse>(`${URL}/${recordingId}/uploads/init`, {
      data: {
        ...(hasCamera ? { camera_content_type: 'video/webm' } : {}),
        recording_content_type: 'video/webm',
      },
      params: recordingSpaceParams(spaceId),
    }),
  retryRecording: (id: string) => post<RecordingRetryResponse>(`${URL}/${id}/retry`),
  shareRecording: (id: string) => post<RecordingSharingResponse>(`${URL}/${id}/share`),
  startRecordingRender: (id: string, data: RecordingRenderRequest) =>
    post<RecordingRenderResponse>(`${URL}/${id}/render`, { data }),
  uploadRecordingChunk,
  createRecording: (data: CreateRecordingRequest) => post<Recording>(`${URL}/`, { data }),
  deleteRecording: (id: string) => del<void>(`${URL}/${id}`),
  getCompositions: (id: string) => get<CompositionsResponse>(`${URL}/${id}/compositions`),
  getRecording: (id: string) => get<Recording>(`${URL}/${id}`),
  getRecordingEditorData: (id: string) => get<RecordingEditorDataResponse>(`${URL}/${id}/editor-data`),
  getSharedRecording: ({ sharing_id }: { sharing_id: string }) =>
    getPublic<RecordingSharedSchema>(`${URL}/shared/${sharing_id}`),
  updateCompositions: (id: string, data: CompositionsResponse) =>
    put<RecordingCompositionsResponse>(`${URL}/${id}/compositions`, { data }),
  updateRecording: (id: string, data: { title: string }) => patch<Recording>(`${URL}/${id}`, { data }),
};
